// Helper functions to create scale and value converters
const createScaleConverter = ( maxValue ) => ( value ) => {
  const rounded = Math.round( value )
  return Number( Math.round( ( 100 / maxValue ) * ( rounded || 0 ) ) );
};

const createValueGetter = () => ( value ) => {
  return value || 0;
};

// Overall Health Score
const getOverallHealthColor = ( value ) => {
  if ( !value && value !== 0 ) return 'gray';    // loading/unknown state
  if ( value > 77.5 ) return '#A6E177';          // very good (very light green)
  if ( value > 65 ) return '#50BA47';            // good (light green)
  if ( value > 45 ) return '#F2BF3A';            // moderate (yellow)
  if ( value > 25 ) return '#FFA500';            // poor (orange)
  return '#F34848';                            // very poor (red)
};

const getOverallHealthStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value > 77.5 ) return 'Very Good';
  if ( value > 65 ) return 'Good';
  if ( value > 45 ) return 'Moderate';
  if ( value > 25 ) return 'Poor';
  return 'Very Poor';
};

const getOverallHealthStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value > 77.5 ) return 5;
  if ( value > 65 ) return 4;
  if ( value > 45 ) return 3;
  if ( value > 25 ) return 2;
  return 1;
};

const overallHealthScale = createScaleConverter( 100 );
const overallHealthValue = createValueGetter();

// Heart Health Functions
const getHeartHealthColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value >= 6.6 ) return '#F34848';    // very high risk
  if ( value >= 3.3 ) return '#FF8C00';    // high risk
  if ( value >= 2.39 ) return '#F2BF3A';   // moderate risk
  if ( value >= 1.65 ) return '#50BA47';   // low risk
  return '#A6E177';                      // very low risk
};

const getHeartHealthStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value >= 6.6 ) return 'Very High Risk';
  if ( value >= 3.3 ) return 'High Risk';
  if ( value >= 2.39 ) return 'Moderate Risk';
  if ( value >= 1.65 ) return 'Low Risk';
  return 'Very Low Risk';
};

const getHeartHealthStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value >= 6.6 ) return 1;
  if ( value >= 3.3 ) return 2;
  if ( value >= 2.39 ) return 3;
  if ( value >= 1.65 ) return 4;
  return 5;
};


const heartHealthScale = createScaleConverter( 100 );
const heartHealthValue = createValueGetter();

// Body Function Functions
const getBodyFunctionColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value >= 4.5 ) return '#A6E177';    // excellent
  if ( value >= 3.5 ) return '#50BA47';    // good
  if ( value >= 2.5 ) return '#F2BF3A';    // moderate
  if ( value >= 1.5 ) return '#FF8C00';    // fair
  return '#F34848';                      // poor
};

const getBodyFunctionStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value >= 4.5 ) return 'Excellent';
  if ( value >= 3.5 ) return 'Good';
  if ( value >= 2.5 ) return 'Moderate';
  if ( value >= 1.5 ) return 'Fair';
  return 'Poor';
};

const getBodyFunctionValueStatusByNo = ( value ) => {
  if ( value == null || value === '' ) return 0;  // Represents "Loading..."
  if ( value >= 4.5 ) return 5; // Excellent
  if ( value >= 3.5 ) return 4; // Good
  if ( value >= 2.5 ) return 3; // Moderate
  if ( value >= 1.5 ) return 2; // Fair

  return 1;  // Poor
};

const bodyFunctionScale = createScaleConverter( 5 );
const bodyFunctionValue = createValueGetter();

// Physical Health Functions
const getPhysicalHealthColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value >= 4.5 ) return '#A6E177';     // excellent
  if ( value >= 3 ) return '#50BA47';     // good
  if ( value >= 2 ) return '#F2BF3A';     // moderate
  if ( value >= 1 ) return '#FF8C00';     // fair
  return '#F34848';                      // poor
};

const getPhysicalHealthStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value >= 4.5 ) return 'Excellent';
  if ( value >= 3 ) return 'Good';
  if ( value >= 2 ) return 'Moderate';
  if ( value >= 1 ) return 'Fair';
  return 'Poor';
};

const getPhysicalHealthStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value >= 4.5 ) return 5;
  if ( value >= 3 ) return 4;
  if ( value >= 2 ) return 3;
  if ( value >= 1 ) return 2;
  return 1;
};

const physicalHealthScale = createScaleConverter( 5 );
const physicalHealthValue = createValueGetter();


const getDiseaseRiskColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value >= 29 ) return '#F34848';   // very high risk
  if ( value >= 19 ) return '#FF8C00';     // high risk
  if ( value >= 15 ) return '#F2BF3A';     // moderate risk
  if ( value >= 9 ) return '#50BA47';     // low risk
  return '#A6E177';                      // very low risk
}

const getDiseaseRiskStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value >= 29 ) return 'Very High Risk';
  if ( value >= 19 ) return 'High Risk';
  if ( value >= 15 ) return 'Moderate Risk';
  if ( value >= 9 ) return 'Low Risk';
  return 'Very Low Risk';
};

const getDiseaseRiskStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value >= 29 ) return 1
  if ( value >= 19 ) return 2
  if ( value >= 15 ) return 3
  if ( value >= 9 ) return 4
  return 5
};

const diseaseRiskScale = createScaleConverter( 5 );
const diseaseRiskValue = createValueGetter();

// Liver Health Functions
const getLiverHealthColor = ( value ) => {
  if ( !value && value !== 0 ) return 'gray';
  if ( value > 77.5 ) return '#F34848';     // very high
  if ( value > 55 ) return '#FFA500';       // high
  if ( value > 45 ) return '#FFFF00';       // moderate
  if ( value > 25 ) return '#008000';       // low
  return '#90EE90';                       // very low
};

const getLiverHealthStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value > 77.5 ) return 'very high';
  if ( value > 55 ) return 'high';
  if ( value > 45 ) return 'moderate';
  if ( value > 25 ) return 'low';
  return 'very low';
};


const liverHealthScale = createScaleConverter( 100 );
const liverHealthValue = createValueGetter();


const getDiabetesRiskColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value < 25 ) return '#A6E177';  // very low
  if ( value >= 25 && value < 45 ) return '#50BA47';  // low
  if ( value >= 45 && value < 55 ) return '#F2BF3A';    // moderate risk
  if ( value >= 55 && value < 77.5 ) return '#FF8C00'; // high risk
  if ( value >= 77.5 && value <= 100 ) return '#F34848'; // very high risk
  return '#F34848';                      // very high risk
};



const getDiabetesRiskStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value < 25 ) return "Very Low Risk"
  if ( value >= 25 && value < 45 ) return 'Low Risk';  // low
  if ( value >= 45 && value < 55 ) return 'Moderate Risk';    // moderate risk
  if ( value >= 55 && value < 77.5 ) return 'High Risk'; // high risk
  if ( value >= 77.5 && value <= 100 ) return 'Very High Risk'; // very high risk
  return 'Very High Risk';
};

const getDiabetesScoreStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value < 25 ) return 5;
  if ( value >= 25 && value < 45 ) return 4;
  if ( value >= 45 && value < 55 ) return 3;
  if ( value >= 55 && value < 77.5 ) return 2;
  if ( value >= 77.5 && value <= 100 ) return 1;
  return 1;
};

const diabetesRiskScale = createScaleConverter( 100 );
const diabetesRiskValue = createValueGetter();

//mental health 
const getMantalScoreStatusByNo = ( value ) => {
  if ( value === undefined || value === null || isNaN( value ) ) return 0;
  if ( value >= 0 && value < 1.0 ) return 1;
  if ( value >= 1.0 && value < 2.0 ) return 1;
  if ( value >= 2.0 && value < 3.0 ) return 2;
  if ( value >= 3.0 && value < 4.0 ) return 3;
  if ( value >= 4.0 && value < 5.0 ) return 4;
  if ( value >= 5.0 ) return 5;
  return 5;
};

// Skin Health Functions
const getSkinHealthColor = ( value ) => {
  if ( !value && value !== 0 ) return '#gray';
  if ( value >= 80 ) return '#A6E177';    // excellent (very light green)
  if ( value >= 65 ) return '#50BA47';    // good (light green)
  if ( value >= 45 ) return '#F2BF3A';    // moderate (yellow)
  if ( value >= 25 ) return '#FFA500';    // fair (orange)
  return '#F34848';                     // poor (red)
};

const getSkinHealthStatus = ( value ) => {
  if ( !value && value !== 0 ) return 'Loading...';
  if ( value >= 80 ) return 'Excellent';
  if ( value >= 65 ) return 'Good';
  if ( value >= 45 ) return 'Moderate';
  if ( value >= 25 ) return 'Fair';
  return 'Poor';
};

const getSkinHealthStatusByNo = ( value ) => {
  if ( !value && value !== 0 ) return 0;
  if ( value >= 80 ) return 5;  // Excellent
  if ( value >= 65 ) return 4;  // Good
  if ( value >= 45 ) return 3;  // Moderate
  if ( value >= 25 ) return 2;  // Fair
  return 1;                   // Poor
};

const skinHealthScale = createScaleConverter( 100 );
const skinHealthValue = createValueGetter();


export const getAcneSeverityScore = ( acneLevel ) => {
  // Based on your CNN model with ~68% accuracy
  // Score reflects skin health (higher = better)
  switch ( acneLevel?.toLowerCase() ) {
    case 'clear':
    case 'low': return 85; // Excellent skin health
    case 'mild':
    case 'moderate': return 50; // Average skin health  
    case 'severe': return 15; // Poor skin health
    default: return 0;
  }
};

export const getSkinTypeScore = ( skinType ) => {
  // Based on EfficientNet B0 with 87.10% training accuracy
  // Score reflects skin balance (Normal = optimal)
  switch ( skinType?.toLowerCase() ) {
    case 'normal': return 90; // Optimal balance
    case 'dry': return 65;    // Needs moisture
    case 'oily': return 60;   // Needs oil control
    default: return 50;       // Unknown/Default
  }
};

export const getSkinToneScore = ( toneValue ) => {
  // Based on Fitzpatrick scale (1-6) + your K-means clustering
  // Convert your 1-10 scale to a health score
  const tone = parseInt( toneValue );
  if ( isNaN( tone ) ) return 50;

  // Higher tone quality = better score
  return Math.min( 100, Math.max( 0, tone * 10 ) );
};

// Alternative: Show confidence levels based on model accuracy
export const getModelConfidence = ( metric ) => {
  switch ( metric ) {
    case 'skinType': return 87; // 87.10% training accuracy
    case 'acne': return 68;     // ~68% accuracy
    case 'tone': return 80;     // Estimated based on K-means + KNN
    default: return 75;
  }
};



// Function to get the appropriate functions based on card type
const getHealthFunctions = ( cardType ) => {

  switch ( cardType?.toLowerCase() ) {
    case 'overall health score':
      return {
        getColor: getOverallHealthColor,
        getStatus: getOverallHealthStatus,
        getScaledValue: overallHealthScale,
        getValue: overallHealthValue,
        getScoreStatusByNo: getOverallHealthStatusByNo
      };
    case 'heart health':
      return {
        getColor: getHeartHealthColor,
        getStatus: getHeartHealthStatus,
        getScaledValue: heartHealthScale,
        getValue: heartHealthValue,
        getScoreStatusByNo: getHeartHealthStatusByNo
      };
    case 'body function':
      return {
        getColor: getBodyFunctionColor,
        getStatus: getBodyFunctionStatus,
        getScaledValue: bodyFunctionScale,
        getValue: bodyFunctionValue,
        getScoreStatusByNo: getBodyFunctionValueStatusByNo
      };
    case 'physical health':
      return {
        getColor: getPhysicalHealthColor,
        getStatus: getPhysicalHealthStatus,
        getScaledValue: physicalHealthScale,
        getValue: physicalHealthValue,
        getScoreStatusByNo: getPhysicalHealthStatusByNo
      };
    case 'overall disease risk':
      return {
        getColor: getDiseaseRiskColor,
        getStatus: getDiseaseRiskStatus,
        getScaledValue: diseaseRiskScale,
        getValue: diseaseRiskValue,
        getScoreStatusByNo: getDiseaseRiskStatusByNo
      };
    case 'liver health':
      return {
        getColor: getLiverHealthColor,
        getStatus: getLiverHealthStatus,
        getScaledValue: liverHealthScale,
        getValue: liverHealthValue
      };
    case 'diabetes risk':
    case 'metabolic health':
      return {
        getColor: getDiabetesRiskColor,
        getStatus: getDiabetesRiskStatus,
        getScaledValue: diabetesRiskScale,
        getValue: diabetesRiskValue,
        getScoreStatusByNo: getDiabetesScoreStatusByNo
      };
    case 'skin health':
      return {
        getColor: getSkinHealthColor,
        getStatus: getSkinHealthStatus,
        getScaledValue: skinHealthScale,
        getValue: skinHealthValue,
        getScoreStatusByNo: getSkinHealthStatusByNo
      };
    default:
      return {
        getColor: () => '#gray',
        getStatus: () => 'Unknown',
        getScaledValue: ( value ) => value,
        getValue: ( value ) => value || 0
      };
  }
};

export default getHealthFunctions;


// Update sharing functions to use the image URL directly
export const shareOnWhatsApp = ( imageUrl ) => {
  const text = 'Check out my health status';
  const whatsAppShareUrl = `https://api.whatsapp.com/send?text=${ encodeURIComponent( `${ text } ${ imageUrl }` ) }`;
  window.open( whatsAppShareUrl, '_blank' );
};

export const shareOnFacebook = ( imageUrl ) => {
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${ encodeURIComponent( imageUrl ) }`;
  window.open( facebookShareUrl, '_blank' );
};

export const shareOnTwitter = ( imageUrl ) => {
  const text = 'Check out my health status';
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${ encodeURIComponent( text ) }&url=${ encodeURIComponent( imageUrl ) }`;
  window.open( twitterShareUrl, '_blank' );
};


export const shareOnSnapchat = ( imageUrl ) => {
  const snapchatShareUrl = `https://www.snapchat.com/scan?attachmentUrl=${ encodeURIComponent( imageUrl ) }`;
  window.open( snapchatShareUrl, '_blank' );
};


export const ratingObjsAcneHealth = {
  poor: {
    text: 'Severe',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
  moderate: {
    text: 'Moderate',
    color: '#FFA500',
    icon: '/others/do-not-enter.svg',
  },
  good: {
    text: 'Clear',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
};

export const ratingObjsToneQuality = {
  poor: { text: 'Uneven', color: '#FF0000', icon: '/others/cross.svg' },
  fair: { text: 'Fair', color: '#FFA500', icon: '/others/do-not-enter.svg' },
  good: { text: 'Good', color: '#F2BF3A', icon: '/others/mark1.svg' },
  very_good: { text: 'Very Good', color: '#50BA47', icon: '/others/mark1.svg' },
  excellent: { text: 'Even', color: '#A6E177', icon: '/others/mark.svg' },
};

export const ratingObjsSkinBalance = {
  needs_care: { text: 'Needs Care', color: '#FFA500', icon: '/others/do-not-enter.svg' },
  balanced: { text: 'Balanced', color: '#50BA47', icon: '/others/mark1.svg' },
  optimal: { text: 'Optimal', color: '#A6E177', icon: '/others/mark.svg' },
};

export const ratingsObjs = {
  'very low': {
    text: 'Very Low',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  'low normal': {
    text: 'Low Normal',
    color: '#F2D53A',
    icon: '/others/do-not-enter.svg',
  },
  low: {
    text: 'Low',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
  normal: {
    text: 'Normal',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  optimal: {
    text: 'Optimal',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  'high normal': {
    text: 'High Normal',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  elevated: {
    text: 'Elevated',
    color: '#FFA500',
    icon: '/others/do-not-enter.svg',
  },
  high: {
    text: 'High',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
};

export const ratingObjsRisk = {
  'very low': {
    text: 'Very Low',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  low: {
    text: 'Low',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  moderate: {
    text: 'Moderate',
    color: '#50BA47',
    icon: '/others/do-not-enter.svg',
  },
  high: {
    text: 'High',
    color: '#FFA500',
    icon: '/others/do-not-enter.svg',
  },
  'very high': {
    text: 'Very High',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
};

export const ratingsObjAge = {
  younger: {
    text: 'Younger',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  normal: {
    text: 'Normal',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  older: {
    text: 'Older',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
};

export const ratingObjsBmi = {
  underweight: {
    text: 'Underweight',
    color: '#50BA47',
    icon: '/others/do-not-enter.svg',
  },
  normal: {
    text: 'Normal',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  overweight: {
    text: 'Overweight',
    color: '#FFD500',
    icon: '/others/do-not-enter.svg',
  },
  obese: {
    text: 'Obese',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
};

export const ratingObjsScore = {
  poor: {
    text: 'Poor',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
  good: {
    text: 'Good',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  'very good': {
    text: 'Very Good',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
};

export const ratingObjsText = {
  clear: {
    text: 'Clear',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  mild: {
    text: 'Mild',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  moderate: {
    text: 'Moderate',
    color: '#F2BF3A',
    icon: '/others/do-not-enter.svg',
  },
  severe: {
    text: 'Severe',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
  dry: {
    text: 'Dry',
    color: '#F2BF3A',
    icon: '/others/do-not-enter.svg',
  },
  normal: {
    text: 'Normal',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  combination: {
    text: 'Combination',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  oily: {
    text: 'Oily',
    color: '#F2BF3A',
    icon: '/others/do-not-enter.svg',
  },
};

export const ratingObjsNumeric = {
  poor: {
    text: 'Poor',
    color: '#FF0000',
    icon: '/others/cross.svg',
  },
  fair: {
    text: 'Fair',
    color: '#FFA500',
    icon: '/others/do-not-enter.svg',
  },
  good: {
    text: 'Good',
    color: '#50BA47',
    icon: '/others/mark1.svg',
  },
  very_good: {
    text: 'Very Good',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
  excellent: {
    text: 'Excellent',
    color: '#A6E177',
    icon: '/others/mark.svg',
  },
};

export const getRatingObjs = scheme => {
  switch ( scheme ) {
    case 'risk':
      return ratingObjsRisk;
    case 'age':
      return ratingsObjAge;
    case 'bmi':
      return ratingObjsBmi;
    case 'score':
    case 'numeric':
      return ratingObjsScore;
    case 'text':
      return ratingObjsText;
    case 'acne_health':        // Add this
      return ratingObjsAcneHealth;
    case 'tone_quality':       // Add this
      return ratingObjsToneQuality;
    case 'skin_balance':       // Add this
      return ratingObjsSkinBalance;
    default:
      return ratingsObjs;
  }
};
export const getRatingGood = ( scheme, rating ) => {
  switch ( scheme ) {
    case 'risk':
      return [ 'very low', 'low' ].includes( rating );
    case 'age':
      return rating !== 'older';
    case 'bmi':
      return [ 'underweight', 'normal' ].includes( rating );
    case 'score':
    case 'numeric':
      return [ 'good', 'very_good', 'excellent' ].includes( rating );
    case 'text':
      return [ 'clear', 'mild', 'normal' ].includes( rating );
    default:
      return [ 'normal', 'high normal', 'low normal', 'optimal' ].includes( rating );
  }
};

export const ratingsToObj = ( ratings, value ) => {
  if ( value === undefined ) {
    return null;
  }

  if ( !ratings ) {
    return null;
  }

  const ratingName = [ ...Object.keys( ratings ) ].find( key => {
    // Handle both array format [min, max] and text format ['value1', 'value2']
    if ( ratings.scheme === 'text' ) {
      return ratings[ key ].includes( value );
    }
    return ratings[ key ][ 0 ] <= value && value <= ratings[ key ][ 1 ];
  } );

  if ( ratingName === undefined ) {
    return null;
  }

  const rating = getRatingObjs( ratings.scheme )[ ratingName ];

  // Check if rating exists before setting properties
  if ( !rating ) {
    return null;
  }

  // Create a copy to avoid mutating the original object
  const ratingCopy = { ...rating };
  ratingCopy.needsImprovement = !getRatingGood( ratings.scheme, ratingName );

  return ratingCopy;
};

// Function to get skin data from localStorage
export const getSkinDataFromStorage = ( metric ) => {
  try {
    // Changed from 'skinResult' to 'skynResult'
    const skinResult = JSON.parse( localStorage.getItem( 'skynResult' ) || '{}' );

    switch ( metric ) {
      case 'acne':
        return skinResult.acne || 'Unknown';
      case 'tone':
        return parseInt( skinResult.tone ) || 0;
      case 'type':
        return skinResult.type || 'Unknown';
      default:
        return skinResult[ metric ] || 0;
    }
  } catch ( error ) {
    console.error( 'Error reading skin data from localStorage:', error );
    return metric === 'tone' ? 0 : 'Unknown';
  }
};
// Function to calculate overall skin health score
export const calculateSkinHealthScore = () => {
  try {
    // Changed from 'skinResult' to 'skynResult'
    const skinResult = JSON.parse( localStorage.getItem( 'skynResult' ) || '{}' );

    let score = 50; // Base score

    // Rest of your function remains the same...
    if ( skinResult.acne ) {
      switch ( skinResult.acne.toLowerCase() ) {
        case 'clear':
          score += 20;
          break;
        case 'mild':
          score += 10;
          break;
        case 'moderate':
          score -= 5;
          break;
        case 'severe':
          score -= 15;
          break;
      }
    }

    if ( skinResult.tone ) {
      const toneValue = parseInt( skinResult.tone );
      score += ( toneValue - 5 ) * 3;
    }

    return Math.max( 0, Math.min( 100, Math.round( score ) ) );
  } catch ( error ) {
    console.error( 'Error calculating skin health score:', error );
    return 50;
  }
};